import React, { useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import AuthShell from "../components/AuthShell";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";

function roleHome(role) {
  if (role === "admin") return "/admin";
  if (role === "licencie") return "/licencie";
  if (role === "commercant") return "/commercant";
  return "/";
}

export default function ForgotPassword() {
  const { user } = useAuth();
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState(null);

  if (user) return <Navigate to={roleHome(user.role)} replace />;

  const onSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await api.post("/auth/forgot-password", { email: email.trim() });
      setSent(true);
    } catch (err) {
      // Even on backend errors, never reveal — but show a generic notice.
      setError(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  if (sent) {
    return (
      <AuthShell
        title="Vérifiez votre boîte mail"
        subtitle="Si un compte est associé à cet email, vous recevrez un lien de réinitialisation valide 1 heure."
        footer={
          <span>
            <Link to="/login" className="text-[#397DFF] font-semibold hover:underline" data-testid="forgot-back-login">
              Retour à la connexion
            </Link>
          </span>
        }
      >
        <div
          data-testid="forgot-password-success"
          className="rounded-xl border border-[#BBF7D0] bg-[#F0FDF4] text-[#166534] px-4 py-4 text-sm leading-relaxed"
        >
          <p className="font-semibold mb-1">Email envoyé.</p>
          <p>
            Ouvrez votre boîte mail et cliquez sur le lien reçu pour choisir un nouveau mot de passe.
            Pensez à vérifier vos spams.
          </p>
        </div>
      </AuthShell>
    );
  }

  return (
    <AuthShell
      title="Mot de passe oublié"
      subtitle="Entrez votre email — nous vous enverrons un lien sécurisé pour réinitialiser votre mot de passe."
      footer={
        <span>
          <Link to="/login" className="text-[#397DFF] font-semibold hover:underline" data-testid="forgot-back-login">
            Retour à la connexion
          </Link>
        </span>
      }
    >
      <form onSubmit={onSubmit} className="space-y-5" data-testid="forgot-password-form">
        <div className="space-y-1.5">
          <Label htmlFor="email">Email</Label>
          <Input
            id="email"
            data-testid="forgot-password-email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            autoComplete="email"
          />
        </div>

        {error && (
          <div
            data-testid="forgot-password-error"
            className="px-3 py-2 rounded-lg bg-[#FEF2F2] text-[#B91C1C] text-sm border border-[#FECACA]"
          >
            {error}
          </div>
        )}

        <Button
          type="submit"
          data-testid="forgot-password-submit"
          disabled={submitting}
          className="w-full h-11 bg-[#F9C80E] hover:bg-[#DDB10A] text-[#191D1E] font-semibold rounded-xl shadow-[0_4px_14px_rgba(249,200,14,0.32)]"
        >
          {submitting ? "Envoi..." : "Envoyer le lien"}
        </Button>
      </form>
    </AuthShell>
  );
}
